// src/utils/authUserService.js

import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  sendEmailVerification,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { app, db } from "../config/firebaseConfig";
import { handleFirestoreOperation } from "./authUserUtils";
import { getActionCodeSettings } from "./actionCodeSettings";
import {
  getVerificationEmailFailureMessage,
  verificationInboxReminder,
} from "./emailDeliverability";
import { sanitizeErrorForLogs, sanitizeDescriptionForLogs } from "./logSanitizer";

const functions = getFunctions(app, "europe-west2");

const VERIFICATION_RESEND_COOLDOWN_MS = 90 * 1000;
const VERIFICATION_RESEND_WINDOW_MS = 24 * 60 * 60 * 1000;
const VERIFICATION_RESEND_MAX_PER_WINDOW = 4;

const getResendStorageKey = (uid) => `zedi:verification:resends:${uid}`;

const readResendState = (uid) => {
  if (!uid || typeof window === "undefined" || !window.localStorage) return [];
  try {
    const raw = window.localStorage.getItem(getResendStorageKey(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => Number.isFinite(v)) : [];
  } catch (_) {
    return [];
  }
};

const writeResendState = (uid, timestamps) => {
  if (!uid || typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(getResendStorageKey(uid), JSON.stringify(timestamps));
  } catch (_) {
    // Ignore storage quota/privacy mode issues.
  }
};

const formatWait = (ms) => {
  const mins = Math.max(1, Math.ceil(ms / 60000));
  return mins === 1 ? "about 1 minute" : `about ${mins} minutes`;
};

const checkResendGate = (uid) => {
  const now = Date.now();
  const recent = readResendState(uid).filter((ts) => now - ts < VERIFICATION_RESEND_WINDOW_MS);

  if (recent.length) {
    const lastSentAt = recent[recent.length - 1];
    const cooldownRemaining = VERIFICATION_RESEND_COOLDOWN_MS - (now - lastSentAt);
    if (cooldownRemaining > 0) {
      writeResendState(uid, recent);
      return {
        allowed: false,
        message: `Please wait ${formatWait(cooldownRemaining)} before requesting another verification email.`,
      };
    }
  }

  if (recent.length >= VERIFICATION_RESEND_MAX_PER_WINDOW) {
    const windowRemaining = VERIFICATION_RESEND_WINDOW_MS - (now - recent[0]);
    writeResendState(uid, recent);
    return {
      allowed: false,
      message: `Too many verification requests in a short period. Please wait ${formatWait(windowRemaining)} and check your inbox/Junk folder.`,
    };
  }

  return { allowed: true, recent, now };
};

const recordResend = (uid, recent, sentAt) => {
  if (!uid) return;
  const updated = [...(recent || []), sentAt];
  writeResendState(uid, updated);
};
  
  /**
   * Log in a user with email & password.
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{ user: import("firebase/auth").User, token: string }>}
   */
  export const loginUser = async (email, password) => {
    const auth = getAuth();
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const token = await cred.user.getIdToken();
      return { user: cred.user, token };
    } catch (error) {
      console.error("❌ loginUser failed:", sanitizeErrorForLogs(error));
      throw error;
    }
  };
  
  /**
   * Register a new user with email & password.
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{ user: import("firebase/auth").User }>}
   */
  export const registerUser = async (email, password) => {
    const auth = getAuth();
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      return { user: cred.user };
    } catch (error) {
      console.error("❌ registerUser failed:", sanitizeErrorForLogs(error));
      throw error;
    }
  };
  
  /**
   * Send a password reset email.
   * @param {string} email
   * @returns {Promise<void>}
   */
  export const sendPasswordReset = async (email) => {
    const auth = getAuth();
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("❌ sendPasswordReset failed:", sanitizeErrorForLogs(error));
      throw error;
    }
  };
  
  /**
   * Log out the current user.
   * @returns {Promise<void>}
   */
  export const logoutUser = async () => { 
    const auth = getAuth();
    try {
      await signOut(auth);
    } catch (error) {
      console.error("❌ logoutUser failed:", sanitizeErrorForLogs(error));
      throw error;
    }
  };
  
  // ========================================
  // Retailer profile operations
  
  /**
   * Fetch retailer data from Firestore.
   * @param {string} userId
   * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
   */
  export const fetchUserData = async (userId) => {
    if (!userId) return { success: false, message: "User ID is required." };
    return await handleFirestoreOperation(async () => {
      const ref = doc(db, "zedipass_retailers", userId);
      const snap = await getDoc(ref);
      return snap.exists()
        ? { success: true, data: snap.data() }
        : { success: false, message: "No retailer profile found." };
    }, sanitizeDescriptionForLogs(`Fetching retailer profile for ${userId}`));
  };
  
  /**
   * Save or update retailer profile in Firestore.
   *
   * TODO(retailer-schema): the initial retailer create on signup should go
   * through the `provisionRetailerCallable` Cloud Function instead of
   * writing Firestore directly. That callable runs the canonical
   * retailerProvisioner (Backend/functions/src/onboarding/retailerProvisioner.ts)
   * which enforces the shape documented in
   * Backend/docs/retailer-document-schema.md and bootstraps settings/appAccess
   * + custom claims. Once the signup flow is migrated, this function should
   * be reduced to update-only operations on already-provisioned retailers.
   *
   * @param {string} userId
   * @param {Object} userData
   * @returns {Promise<{success: boolean, message: string}>}
   */
  export const saveUserProfile = async (userId, userData) => {
    if (!userId || !userData) return { success: false, message: "User ID and data required." };
    return await handleFirestoreOperation(async () => {
      await setDoc(doc(db, "zedipass_retailers", userId), userData, { merge: true });
      return { success: true, message: "Retailer profile updated." };
    }, sanitizeDescriptionForLogs(`Saving retailer profile for ${userId}`));
  };
  
  /**
   * Resend email verification for the current user.
   * @returns {Promise<{success: boolean, message: string}>}
   */
  export const resendVerificationEmail = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return { success: false, message: "No authenticated user." };

    const gate = checkResendGate(user.uid);
    if (!gate.allowed) return { success: false, message: gate.message };

    try {
      await sendEmailVerification(user, getActionCodeSettings());
      recordResend(user.uid, gate.recent, gate.now);
      return {
        success: true,
        message: `Verification email sent. ${verificationInboxReminder}`,
      };
    } catch (error) {
      console.error("❌ resendVerificationEmail failed:", sanitizeErrorForLogs(error));
      return {
        success: false,
        code: error?.code || "auth/verification-email-failed",
        message: getVerificationEmailFailureMessage(error),
      };
    }
  };
  
  /**
   * Delete retailer profile from Firestore.
   * @param {string} userId
   * @returns {Promise<{success: boolean, message: string}>}
   */
  export const deleteUserProfile = async (userId) => {
    if (!userId) return { success: false, message: "User ID is required." };
    return await handleFirestoreOperation(async () => {
      await deleteDoc(doc(db, "zedipass_retailers", userId));
      return { success: true, message: "Retailer profile deleted." };
    }, sanitizeDescriptionForLogs(`Deleting retailer profile for ${userId}`));
  };

  /**
   * Fully delete the authenticated retailer account via Cloud Function cleanup.
   * @returns {Promise<{success: boolean, message: string}>}
   */
  export const deleteRetailerAccount = async () => {
    try {
      const callable = httpsCallable(functions, "gdprDeleteUser");
      const response = await callable();
      const message = response?.data?.message || "Retailer account deleted.";
      return { success: true, message };
    } catch (error) {
      console.error("❌ deleteRetailerAccount failed:", sanitizeErrorForLogs(error));
      const message =
        error?.details?.message || error?.message || "Failed to delete retailer account.";
      return { success: false, message };
    }
  };
